import React, {
  createContext,
  useState,
  useReducer,
  useEffect,
  useRef,
} from "react";

// Create context for data
export const DataContext = createContext();

export const DataProvider = ({ children }) => {
  // State variables to store extracted data
  const [extractedLines, setExtractedLines] = useState([]);
  const [extractedRows, setExtractedRows] = useState(0);
  const [rowLabels, setRowLabels] = useState([]);
  const [extractedColumns, setExtractedColumns] = useState(0);
  const [extractedProjectTitle, setExtractedProjectTitle] = useState([]);
  const [extractedProjectDate, setExtractedProjectDate] = useState([]);
  const [extractedProjectTime, setExtractedProjectTime] = useState([]);
  const [extractedProjectInstrument, setExtractedProjectInstrument] = useState(
    []
  );
  const [extractedAssayPlateBarcode, setExtractedAssayPlateBarcode] = useState(
    []
  );
  const [extractedAddPlateBarcode, setExtractedAddPlateBarcode] = useState([]);
  const [extractedProjectProtocol, setExtractedProjectProtocol] = useState([]);
  const [extractedBinning, setExtractedBinning] = useState([]);
  const [
    extractedIndicatorConfigurations,
    setExtractedIndicatorConfigurations,
  ] = useState([]);
  const [extractedOperator, setExtractedOperator] = useState([]);
  const [extractedIndicatorTimes, setExtractedIndicatorTimes] = useState([]);
  const [analysisData, setAnalysisData] = useState([]);
  const [project, setProject] = useState(null);
  const [wellArrays, setWellArrays] = useState([]);
  const [globalMaxY, setGlobalMaxY] = useState(undefined);
  const [wellArraysUpdated, setWellArraysUpdated] = useState(false);
  const [selectedWellArray, setSelectedWellArray] = useState([]);
  const [overlayRawAndFiltered, setOverlayRawAndFiltered] = useState(false);

  // TESTING
  const [extractedIndicators, setExtractedIndicators] = useState([]);
  const [selectedIndicators, setSelectedIndicators] = useState([0]);

  // state handling selected and enabled filters
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [enabledFilters, setEnabledFilters] = useState([]);

  // state handling new uploaded filters
  const [uploadedFilters, setUploadedFilters] = useState([]);

  // state determining what data is shown in minigraph-grid (rawData or filteredData)
  const [showFiltered, setShowFiltered] = useState(false);

  // state handling saved metrics
  const [savedMetrics, setSavedMetrics] = useState([]);

  // state handling annotations
  const [annotations, setAnnotations] = useState([]);

  // new state for max points
  const [maxPoints, setMaxPoints] = useState(0);

  useEffect(() => {
    // Compute wellArrays whenever the project changes
    const plate = project?.plate || [];
    const experiment = plate[0]?.experiments[0] || {};
    const updatedWellArrays = experiment.wells || [];
    setWellArrays(updatedWellArrays); // Update wellArrays based on current project

    // Calculate maxPoints for all displayed indicators (raw and filtered).
    // Reads typed-array length directly when available so we don't trigger
    // a {x,y}[] materialization across every well on huge files.
    let maxCount = 0;
    for (let w = 0; w < updatedWellArrays.length; w++) {
      const inds = updatedWellArrays[w].indicators || [];
      for (let i = 0; i < inds.length; i++) {
        const ind = inds[i];
        if (!ind || !ind.isDisplayed) continue;
        const rawLen = ind.rawYs
          ? ind.rawYs.length
          : Array.isArray(ind.rawData)
          ? ind.rawData.length
          : 0;
        const filteredLen = ind.filteredYs
          ? ind.filteredYs.length
          : Array.isArray(ind.filteredData)
          ? ind.filteredData.length
          : 0;
        if (rawLen > maxCount) maxCount = rawLen;
        if (filteredLen > maxCount) maxCount = filteredLen;
      }
    }
    setMaxPoints(maxCount);

    // Calculate globalMaxY across all wells and indicators. Walks raw and
    // filtered typed arrays directly — never forces materialization of
    // either {x,y}[] view, which would OOM on huge files.
    let maxY = -Infinity;
    for (let w = 0; w < updatedWellArrays.length; w++) {
      const inds = updatedWellArrays[w].indicators || [];
      for (let i = 0; i < inds.length; i++) {
        const ind = inds[i];
        if (!ind) continue;
        const rYs = ind.rawYs;
        if (rYs) {
          for (let j = 0; j < rYs.length; j++) {
            if (rYs[j] > maxY) maxY = rYs[j];
          }
        } else if (Array.isArray(ind.rawData)) {
          const rd = ind.rawData;
          for (let j = 0; j < rd.length; j++) {
            const y = rd[j] && rd[j].y;
            if (typeof y === "number" && y > maxY) maxY = y;
          }
        }
        const fYs = ind.filteredYs;
        if (fYs) {
          for (let j = 0; j < fYs.length; j++) {
            if (fYs[j] > maxY) maxY = fYs[j];
          }
        } else if (Array.isArray(ind.filteredData)) {
          const fd = ind.filteredData;
          for (let j = 0; j < fd.length; j++) {
            const y = fd[j] && fd[j].y;
            if (typeof y === "number" && y > maxY) maxY = y;
          }
        }
      }
    }
    setGlobalMaxY(maxY === -Infinity ? undefined : maxY);
  }, [project]);

  // Function to set wellArrays from outside
  const updateWellArrays = (newWellArrays) => {
    setWellArrays(newWellArrays);
  };

  // functions handling selectedWellArray management
  const handleSelectWell = (well) => {
    setSelectedWellArray((prevArray) => [...prevArray, well]);
  };

  const handleDeselectWell = (wellIdToRemove) => {
    setSelectedWellArray((prevArray) =>
      prevArray.filter((well) => well.id !== wellIdToRemove)
    );
  };

  const handleClearSelectedWells = () => {
    setSelectedWellArray([]);
  };

  // Generate row labels based on extractedRows
  const generateRowLabels = (extractedRows) => {
    const labels = [];
    for (let i = 0; i < extractedRows; i++) {
      let label = "";
      let n = i;
      while (n >= 0) {
        label = String.fromCharCode((n % 26) + 65) + label;
        n = Math.floor(n / 26) - 1;
      }
      labels.push(label);
    }
    return labels;
  };

  // Function to extract project title from content

  const extractProjectTitle = (content) => {
    const lines = content.split("\n"); // Split content into lines
    const startIndex = lines.findIndex((line) => line.includes("Project")); // Find the line containing 'Project'

    if (startIndex !== -1) {
      // Split the line by tab and return the second part (after 'Project')
      const projectLine = lines[startIndex];
      const projectParts = projectLine.split("\t"); // Split by tab

      if (projectParts.length > 1) {
        return projectParts[1].trim(); // Return the project title without extra spaces or characters
      }
    }

    return ""; // Return an empty string if no project title is found
  };

  // Function to extract project date from content
  const extractProjectDate = (content) => {
    const lines = content.split("\n"); // Split content into lines
    const startIndex = lines.findIndex((line) => line.includes("Date")); // Find the line containing 'Date'

    if (startIndex !== -1) {
      // Split the line by tab and return the second part (the date)
      const dateLine = lines[startIndex];
      const dateParts = dateLine.split("\t"); // Split by tab

      if (dateParts.length > 1) {
        return dateParts[1].trim(); // Return the date, removing any extra spaces or characters
      }
    }

    return ""; // Return an empty string if no date is found
  };

  const extractProjectTime = (content) => {
    const lines = content.split("\n");
    const startIndex = lines.findIndex((line) => line.includes("Time")); // Find the line with 'Time'

    if (startIndex !== -1) {
      const timeLine = lines[startIndex];
      const timeParts = timeLine.split("\t"); // Split by tab

      if (timeParts.length > 1) {
        return timeParts[1].trim(); // Return the clean time value
      }
    }

    return ""; // Return empty string if not found
  };

  const extractInstrument = (content) => {
    const lines = content.split("\n");
    const startIndex = lines.findIndex((line) => line.includes("Instrument"));

    if (startIndex !== -1) {
      const instrumentLine = lines[startIndex];
      const instrumentParts = instrumentLine.split("\t");

      if (instrumentParts.length > 1) {
        return instrumentParts[1].trim();
      }
    }

    return "";
  };

  const extractProjectProtocol = (content) => {
    const lines = content.split("\n");
    const startIndex = lines.findIndex((line) => line.includes("ProtocolName"));

    if (startIndex !== -1) {
      const protocolLine = lines[startIndex];
      const protocolParts = protocolLine.split("\t");

      if (protocolParts.length > 1) {
        return protocolParts[1].trim();
      }
    }

    return "";
  };

  const extractIndicatorConfigurations = (content) => {
    const lines = content.split("\n");
    const indicatorConfigurations = [];

    lines.forEach((line) => {
      if (line.includes("Indicator")) {
        const parts = line.split("\t");

        if (parts.length >= 9) {
          // Ensure there are enough parts to extract all fields
          const config = {
            name: parts[1].trim(),
            Excitation: parts[3].trim(),
            Emission: parts[5].trim(),
            Exposure: parts[7].trim(),
            Gain: parts[9].trim(),
          };
          indicatorConfigurations.push(config);
        }
      }
    });

    return indicatorConfigurations;
  };

  const extractOperator = (content) => {
    const lines = content.split("\n");
    const startIndex = lines.findIndex((line) => line.includes("NumCols"));
    const endIndex = lines.findIndex((line) => line.includes("Project"));

    if (startIndex !== -1 && endIndex !== -1 && startIndex < endIndex) {
      const operatorLine = lines.slice(startIndex + 1, endIndex)[0];
      // Split by tabs, slice to exclude "Operator", and trim each part
      return operatorLine
        .split("\t")
        .slice(1)
        .map((part) => part.trim());
    }

    return [];
  };

  const extractAssayPlateBarcode = (content) => {
    const lines = content.split("\n");
    const startIndex = lines.findIndex((line) =>
      line.includes("AssayPlateBarcode")
    );

    if (startIndex !== -1) {
      const barcodeLine = lines[startIndex];
      const barcodeParts = barcodeLine.split("\t");

      if (barcodeParts.length > 1) {
        return barcodeParts[1].trim();
      }
    }

    return "";
  };

  const extractAddPlateBarcode = (content) => {
    const lines = content.split("\n");
    const startIndex = lines.findIndex((line) =>
      line.includes("AddPlateBarcode")
    );

    if (startIndex !== -1) {
      const barcodeLine = lines[startIndex];
      const barcodeParts = barcodeLine.split("\t");

      if (barcodeParts.length > 1) {
        return barcodeParts[1].trim();
      }
    }

    return "";
  };

  const extractBinning = (content) => {
    const lines = content.split("\n");
    const startIndex = lines.findIndex((line) => line.includes("Binning"));

    if (startIndex !== -1) {
      const binningLine = lines[startIndex];
      const binningParts = binningLine.split("\t");

      if (binningParts.length > 1) {
        return binningParts[1].trim();
      }
    }

    return "";
  };

  // Function to extract number of rows from content
  const extractNumberOfRows = (content) => {
    const lines = content.split("\n");
    const startIndex = lines.findIndex((line) => line.includes("NumRows"));
    const endIndex = lines.findIndex((line) => line.includes("NumCols"));
    if (startIndex !== -1 && endIndex !== -1 && startIndex < endIndex) {
      const numRowsLine = lines[startIndex];
      return parseInt(numRowsLine.replace(/[^\d]/g, ""), 10); // removes all non-digit characters
    }
    return 0; // Return 0 when NumRows is not found
  };

  // Function to extract number of columns from content
  const extractNumberOfColumns = (content) => {
    const lines = content.split("\n");
    const startIndex = lines.findIndex((line) => line.includes("NumCols"));
    const endIndex = lines.findIndex((line) => line.includes("Operator"));
    if (startIndex !== -1 && endIndex !== -1 && startIndex < endIndex) {
      const numColsLine = lines[startIndex];
      return parseInt(numColsLine.replace(/[^\d]/g, ""), 10); // removes all non-digit characters
    }
    return 0; // Return 0 when NumCols is not found
  };

  const findLinesWith = (lines, matchString) => {
    // const lines = content.split("\n"); // Split content into lines
    let ndxs = [];
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes(matchString)) {
        ndxs.push(i);
      }
      // console.log("findLinesWith: ", lines);
    }
    return ndxs;
  };

  const getItem = (line, ndx) => {
    let tokens = line.split("\t");
    // console.log("getItem tokens: ", tokens[ndx]);
    return tokens[ndx];
  };

  const getIndicators = (content) => {
    const lines = content.split("\n"); // Split content into lines

    let indicators = [];
    let startNdxs = findLinesWith(lines, "<INDICATOR_DATA");
    let endNdxs = findLinesWith(lines, "</INDICATOR_DATA>");

    for (let i = 0; i < startNdxs.length; i++) {
      let indicatorName = getItem(lines[startNdxs[i]], 1); // Assuming 'New Green' is the second tab-separated item
      let startIndex = startNdxs[i];
      let endIndex = endNdxs[i];
      indicators.push({ id: i, indicatorName, startIndex, endIndex });
    }

    // console.log("indicators: ", indicators);
    return indicators;
  };

  const extractLines = (content, indicators) => {
    const lines = content.split("\n");
    let extractedLinesByIndicator = {};

    indicators.forEach(({ indicatorName, startIndex, endIndex }) => {
      extractedLinesByIndicator[indicatorName] = lines.slice(
        startIndex + 2,
        endIndex
      );
    });

    // console.log("Extracted Lines by Indicator:", extractedLinesByIndicator);
    return extractedLinesByIndicator;
  };

  const extractIndicatorTimes = (
    extractedLinesByIndicator,
    extractedRows,
    extractedColumns
  ) => {
    let indicatorTimes = {};
    let analysisData = {};
    const plateDimensions = extractedRows * extractedColumns;

    for (let indicator in extractedLinesByIndicator) {
      const extractedLines = extractedLinesByIndicator[indicator];

      let times = [];
      let dataPoints = [];

      // Iterate through each line in extractedLines
      for (let i = 0; i < extractedLines.length; i++) {
        // Split the line into elements by tab delimiter
        const lineElements = extractedLines[i].split("\t");

        // Push the first element (time) to the times array
        times.push(parseFloat(lineElements[0].replace("\r", "")));

        // Push all other elements (data points) to the dataPoints array
        for (let j = 1; j < lineElements.length; j++) {
          const dataPoint = parseFloat(lineElements[j].replace("\r", ""));
          if (!isNaN(dataPoint)) {
            dataPoints.push(dataPoint);
          }
        }
      }

      // Assign the extracted times and data points to their respective indicators
      indicatorTimes[indicator] = times;
      analysisData[indicator] = dataPoints;
    }

    // Set or return the values as needed
    setExtractedIndicatorTimes(indicatorTimes);
    setAnalysisData(analysisData);
    // console.log(indicatorTimes, analysisData);
    return { indicatorTimes, analysisData };
  };

  // One extract worker per in-flight extractAllData call, terminated when
  // the call settles. A single worker used to be created on every render of
  // this provider and never terminated — Firefox caps dedicated workers per
  // origin (then silently queues new ones, hanging file loads forever) and
  // Chromium leaks the threads until the tab dies.
  const extractWorkersRef = useRef(new Set());

  useEffect(() => {
    const workers = extractWorkersRef.current;
    return () => {
      workers.forEach((w) => w.terminate());
      workers.clear();
    };
  }, []);

  // Accepts either a string (legacy path) or a File/Blob (preferred —
  // streamed in the worker). The worker handles both shapes and returns the
  // same result format. Float64Array fields in the result are zero-copy
  // transferred when the input is a Blob.
  //
  // commitToState: false skips the provider-wide setState calls — batch
  // processing consumes only the returned result and must not push each
  // file's full parse into app state.
  function extractAllData(input, { commitToState = true } = {}) {
    return new Promise((resolve, reject) => {
      const worker = new Worker(
        new URL("../workers/extractWorker.js", import.meta.url)
      );
      extractWorkersRef.current.add(worker);
      const finish = () => {
        extractWorkersRef.current.delete(worker);
        worker.terminate();
      };
      worker.onmessage = (e) => {
        const result = e.data;
        if (result && result.__error) {
          finish();
          reject(new Error(result.message || "extractWorker error"));
          return;
        }
        if (commitToState) {
          setExtractedIndicators(result.extractedIndicators);
          setExtractedLines(result.extractedLines);
          setExtractedRows(result.extractedRows);
          setRowLabels(result.rowLabels);
          setExtractedColumns(result.extractedColumns);
          setExtractedProjectTitle(result.extractedProjectTitle);
          setExtractedProjectDate(result.extractedProjectDate);
          setExtractedProjectTime(result.extractedProjectTime);
          setExtractedProjectInstrument(result.extractedProjectInstrument);
          setExtractedProjectProtocol(result.extractedProjectProtocol);
          setExtractedAssayPlateBarcode(result.extractedAssayPlateBarcode);
          setExtractedAddPlateBarcode(result.extractedAddPlateBarcode);
          setExtractedBinning(result.extractedBinning);
          setExtractedIndicatorConfigurations(
            result.extractedIndicatorConfigurations
          );
          setExtractedOperator(result.extractedOperator);
          setExtractedIndicatorTimes(result.extractedIndicatorTimes);
          setAnalysisData(result.analysisData);
        }
        finish();
        resolve(result);
      };
      worker.onerror = (event) => {
        finish();
        reject(new Error(event.message || "extractWorker crashed"));
      };
      if (input && typeof input.stream === "function") {
        worker.postMessage({ file: input });
      } else {
        worker.postMessage({ content: input });
      }
    });
  }

  return (
    <DataContext.Provider
      value={{
        extractedIndicators,
        extractedLines,
        extractedRows,
        rowLabels,
        setRowLabels,
        extractedColumns,
        extractedProjectTitle,
        extractedProjectDate,
        extractedProjectTime,
        extractedProjectInstrument,
        extractedProjectProtocol,
        extractedAssayPlateBarcode,
        extractedAddPlateBarcode,
        extractedBinning,
        extractedIndicatorConfigurations,
        extractedOperator,
        extractAllData,
        extractIndicatorTimes,
        extractedIndicatorTimes,
        setExtractedIndicatorTimes,
        analysisData,
        setAnalysisData,
        project,
        setProject,
        wellArrays, // Expose wellArrays to all components
        setWellArrays,
        updateWellArrays, // Provide function to update wellArrays to all components
        wellArraysUpdated,
        setWellArraysUpdated,
        showFiltered,
        setShowFiltered,
        selectedWellArray,
        setSelectedWellArray, // <-- Expose this for batch selection
        handleSelectWell,
        handleDeselectWell,
        handleClearSelectedWells,
        selectedFilters,
        setSelectedFilters,
        enabledFilters,
        setEnabledFilters,
        uploadedFilters,
        setUploadedFilters,
        savedMetrics,
        setSavedMetrics,
        annotations,
        setAnnotations,
        overlayRawAndFiltered,
        setOverlayRawAndFiltered,
        maxPoints, // <-- provide maxPoints in context
        setMaxPoints,
        globalMaxY,
        setGlobalMaxY,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
